import {
  measureText,
  styled,
  Tooltip,
  Typography,
  useTypographyVariantFontForMeasuringText,
} from '@chronosphereio/chrono-ui';
import { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useChartsTheme } from '@/components';
import { BarChartData, GaugeChartThresholdOptions, UnitOptions, useFindThreshold } from '@/core';
import {
  buildDatasetValues,
  CONTAINER_VERTICAL_PADDING_PX,
  calculateBarContainerWidth,
  calculateBarHeight,
  calculateFillPct,
  calculateMessageOverlay,
  calculateTopSpacer,
  formatBarGaugeValue,
  hasContentOverflow,
  ROW_GAP_PX,
} from './html-gauge-chart-utils';

interface HtmlBarGaugeBaseProps {
  width: number;
  height: number;
  data: BarChartData[];
  /**
   * Upper bound of the bar gauge's value axis. Each bar's rendered length is
   * `value / max` of the available width, so values equal to `max` fill the
   * track and values above `max` are clamped. Also used to pick the active
   * threshold bucket for coloring.
   */
  max: number;
  unit?: UnitOptions;
  thresholds?: GaugeChartThresholdOptions;
}

const DEFAULT_THRESHOLDS: GaugeChartThresholdOptions = {};

// Horizontal padding applied to each row so both the virtualized and
// non-virtualized paths match the original's outer `t.spacing(0.5, 2)` layout.
const ROW_HORIZONTAL_PADDING_PX = 16;

// A couple of pixels of slack so text never gets unexpectedly clipped due to
// subpixel rounding between canvas-based measurement and the DOM.
const MEASURED_WIDTH_PADDING_PX = 2;

// Cap fractions for the side columns when text is very long. The label cap
// matches the original ECharts axis label `width: width / 3`; the value cap is
// chosen to leave most of the row to the bar even with long formatted numbers.
const LABEL_MAX_WIDTH_FRACTION = 1 / 3;
const VALUE_MAX_WIDTH_FRACTION = 1 / 6;

// Gap between row siblings (label / bar / value). Mirrored in JS so
// `calculateBarContainerWidth` matches the rendered layout.
const ROW_ITEM_GAP_PX = 8;

// Right-side breathing room between the inline threshold message and the value
// column so the two never visually collide.
const MESSAGE_RIGHT_PAD_PX = 6;

// Label on the left of each row. Its `width` is set inline per-render from the
// canvas-measured column width, so we only bake in the static ellipsis +
// subdued-color styles here.
const LabelTypography = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.text.legacySecondary,
}));

// Formatted value on the right of each row. The threshold color and width are
// passed inline per-render, so only the right-aligned ellipsis styles are
// baked in here.
const ValueTypography = styled(Typography)({
  flexShrink: 0,
  textAlign: 'right',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

/** Threshold step message, overlaid on the bar container. */
const MessageTypography = styled(Typography)({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Per-row flex container; `paddingTop` is set inline since the first row
// uses 0 instead of `ROW_GAP_PX`.
const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: ROW_ITEM_GAP_PX,
  paddingLeft: ROW_HORIZONTAL_PADDING_PX,
  paddingRight: ROW_HORIZONTAL_PADDING_PX,
});

// Bar gray-track container; `height` + `backgroundColor` are dynamic.
const BarTrack = styled('div')({
  position: 'relative',
  flex: 1,
  minWidth: 0,
});

// Colored fill inside `BarTrack`; `width` + `backgroundColor` are dynamic.
const BarFill = styled('div')({
  height: '100%',
});

// Header/Footer spacers for Virtuoso. `height` is dynamic.
const Spacer = styled('div')({});

export const HtmlBarGaugeBase = ({
  width,
  height,
  data,
  max,
  unit,
  thresholds: thresholdsProp,
}: HtmlBarGaugeBaseProps) => {
  const chartsTheme = useChartsTheme();

  const thresholds = thresholdsProp ?? DEFAULT_THRESHOLDS;

  const findThreshold = useFindThreshold({ thresholds, palette: chartsTheme.thresholds, max });

  const { datasetValues, values } = useMemo(() => buildDatasetValues(data), [data]);

  // Missing values render as a muted "--" placeholder rather than a threshold
  // color, both because no threshold actually applies and because users
  // shouldn't be misled into reading "no data" as "0".
  const missingValueColor = chartsTheme.theme.palette.text.disabled;
  const { formattedValues, anyMessages } = useMemo(() => {
    const next: Array<{
      value: string;
      color: string;
      message: string | undefined;
      isMissing: boolean;
    }> = new Array(values.length);
    let hasAnyMessage = false;
    for (let i = 0; i < values.length; i++) {
      const value = values[i] ?? null;
      const { text, isMissing } = formatBarGaugeValue(value, unit);
      const threshold = value === null ? undefined : findThreshold(value);
      if (threshold?.name) {
        hasAnyMessage = true;
      }
      next[i] = {
        value: text,
        color: threshold?.color ?? missingValueColor,
        // Threshold step `name` (set via the gauge thresholds editor's
        // "Message" field, or auto-populated by per-query dynamic
        // thresholds) — rendered inline on each row so the bar surfaces
        // the same context the radial gauge shows via its `message` prop.
        message: threshold?.name,
        isMissing,
      };
    }
    return { formattedValues: next, anyMessages: hasAnyMessage };
  }, [values, unit, findThreshold, missingValueColor]);

  // Use canvas-based text measurement so the side columns are exactly as wide
  // as the widest label / value, leaving the bar column to claim the rest. Caps
  // prevent pathological labels from squeezing the bar to nothing. The per-row
  // measured widths are also returned so we can show a tooltip only on rows
  // whose text actually got truncated by the column cap. Threshold messages
  // share `body2` with the value text so we only need one font measurement.
  const body2Font = useTypographyVariantFontForMeasuringText('body2');

  const { labelColumnWidth, valueColumnWidth, measuredLabelWidths, measuredValueWidths, measuredMessageWidths } =
    useMemo(() => {
      const labelCap = Math.floor(width * LABEL_MAX_WIDTH_FRACTION);
      const valueCap = Math.floor(width * VALUE_MAX_WIDTH_FRACTION);

      const labelWidths: number[] = new Array(datasetValues.length);
      const valueWidths: number[] = new Array(datasetValues.length);
      // Skip allocating per-row message widths when no row has a message;
      // consumers fall back to `?? 0` so an empty array is safe.
      const messageWidths: number[] = anyMessages ? new Array(datasetValues.length) : [];

      let maxLabelWidth = 0;
      let maxValueWidth = 0;

      datasetValues.forEach((datum, idx) => {
        const labelMetrics = measureText(datum.label, body2Font);
        const labelWidth = labelMetrics?.width ?? 0;
        labelWidths[idx] = labelWidth;
        if (labelWidth > maxLabelWidth) {
          maxLabelWidth = labelWidth;
        }

        const valueMetrics = measureText(formattedValues[idx]?.value ?? '', body2Font);
        const valueWidth = valueMetrics?.width ?? 0;
        valueWidths[idx] = valueWidth;
        if (valueWidth > maxValueWidth) {
          maxValueWidth = valueWidth;
        }

        if (anyMessages) {
          const message = formattedValues[idx]?.message ?? '';
          const messageMetrics = message ? measureText(message, body2Font) : undefined;
          messageWidths[idx] = messageMetrics?.width ?? 0;
        }
      });

      return {
        labelColumnWidth: Math.min(labelCap, Math.ceil(maxLabelWidth) + MEASURED_WIDTH_PADDING_PX),
        valueColumnWidth: Math.min(valueCap, Math.ceil(maxValueWidth) + MEASURED_WIDTH_PADDING_PX),
        measuredLabelWidths: labelWidths,
        measuredValueWidths: valueWidths,
        measuredMessageWidths: messageWidths,
      };
    }, [datasetValues, formattedValues, body2Font, width, anyMessages]);

  // Skips vertical centering (see `calculateTopSpacer`) when the data wouldn't
  // fit at the minimum bar height, letting Virtuoso own the scroll area.
  const hasOverflow = hasContentOverflow(data.length, height);

  const barHeight = useMemo(() => calculateBarHeight(data.length, height), [data.length, height]);

  // Virtuoso renders its items absolutely-positioned, so flex-based centering
  // doesn't apply; a leading spacer in the Header slot is the idiomatic
  // equivalent of the original ECharts vertical centering.
  const topSpacer = useMemo(
    () => calculateTopSpacer({ hasOverflow, rowCount: data.length, barHeight, height }),
    [hasOverflow, data.length, barHeight, height]
  );

  const renderRow = useCallback(
    (index: number) => {
      const d = datasetValues[index];
      const formatted = formattedValues[index];
      if (!d || !formatted) return null;

      // Bars render as `value / max` of the available width, matching the
      // ECharts x-axis bound [0, max] in the original implementation.
      const fillPct = calculateFillPct(d.value, max);

      // A row is truncated when the actual canvas-measured text width plus the
      // subpixel padding wouldn't fit in the (capped) column width; only those
      // rows get a tooltip so we don't add hover overhead to every row. Missing
      // values are never tooltipped since the placeholder reveals nothing.
      const labelTruncated = (measuredLabelWidths[index] ?? 0) + MEASURED_WIDTH_PADDING_PX > labelColumnWidth;
      const valueTruncated =
        !formatted.isMissing && (measuredValueWidths[index] ?? 0) + MEASURED_WIDTH_PADDING_PX > valueColumnWidth;

      const labelEl = (
        <LabelTypography variant="body2" style={{ width: labelColumnWidth }}>
          {d.label}
        </LabelTypography>
      );

      const valueEl = (
        <ValueTypography variant="body2" style={{ width: valueColumnWidth, color: formatted.color }}>
          {formatted.value}
        </ValueTypography>
      );

      // Inline threshold message ("Message" field on each threshold step).
      // Missing-value rows never show a message (no active threshold).
      const messageText = formatted.isMissing ? undefined : formatted.message;
      const barContainerWidth = calculateBarContainerWidth({
        panelWidth: width,
        rowHorizontalPadding: ROW_HORIZONTAL_PADDING_PX,
        labelColumnWidth,
        valueColumnWidth,
        stackGap: ROW_ITEM_GAP_PX,
      });
      const messageLayout =
        messageText !== undefined
          ? calculateMessageOverlay({
              barContainerWidth,
              fillPct,
              naturalTextWidth: measuredMessageWidths[index] ?? 0,
              measuredWidthPadding: MEASURED_WIDTH_PADDING_PX,
              rightPad: MESSAGE_RIGHT_PAD_PX,
            })
          : undefined;

      // Use a contrast color when the text sits over the colored fill,
      // threshold color when it sits over the gray track.
      const messageElement =
        messageText !== undefined && messageLayout !== undefined && messageLayout.width > 0 ? (
          <MessageTypography
            variant="body2"
            data-testid="html-bar-gauge-message"
            style={{
              right: MESSAGE_RIGHT_PAD_PX,
              width: messageLayout.width,
              color: messageLayout.insideFill
                ? chartsTheme.theme.palette.getContrastText(formatted.color)
                : formatted.color,
              textAlign: 'right',
            }}
          >
            {messageText}
          </MessageTypography>
        ) : null;

      return (
        <Row role="listitem" data-testid="html-bar-gauge-row" style={{ paddingTop: index === 0 ? 0 : ROW_GAP_PX }}>
          {labelTruncated ? <Tooltip Content={d.label}>{labelEl}</Tooltip> : labelEl}
          <BarTrack
            style={{
              height: barHeight,
              backgroundColor: chartsTheme.theme.palette.background.canvas,
            }}
          >
            <BarFill style={{ width: `${fillPct}%`, backgroundColor: formatted.color }} />
            {messageElement !== null &&
              (messageLayout?.truncated ? (
                <Tooltip Content={messageText ?? ''}>{messageElement}</Tooltip>
              ) : (
                messageElement
              ))}
          </BarTrack>
          {valueTruncated ? <Tooltip Content={formatted.value}>{valueEl}</Tooltip> : valueEl}
        </Row>
      );
    },
    [
      datasetValues,
      formattedValues,
      max,
      barHeight,
      labelColumnWidth,
      valueColumnWidth,
      measuredLabelWidths,
      measuredValueWidths,
      measuredMessageWidths,
      width,
      chartsTheme,
    ]
  );

  // Virtuoso positions items absolutely inside its scroll container, which
  // means we can't add list-level padding via flex/CSS the way the original
  // ECharts wrapper did. The supported workaround is to inject zero-content
  // Header / Footer slots whose heights act as top and bottom padding:
  //   - Header height grows when content fits in the panel, simulating the
  //     vertical centering that ECharts produced by distributing items across
  //     the category axis (see `topSpacer`).
  //   - Footer height keeps a small bottom gutter symmetric with the top
  //     gutter so the last row never sits flush against the panel edge.
  const virtuosoComponents = useMemo(
    () => ({
      Header: () => <Spacer style={{ height: topSpacer }} />,
      Footer: () => <Spacer style={{ height: CONTAINER_VERTICAL_PADDING_PX / 2 }} />,
    }),
    [topSpacer]
  );

  return (
    <Virtuoso
      role="list"
      data-testid="html-bar-gauge"
      style={{ width, height }}
      totalCount={datasetValues.length}
      itemContent={renderRow}
      computeItemKey={(index) => `${datasetValues[index]?.label ?? ''}-${index}`}
      components={virtuosoComponents}
      overscan={200}
    />
  );
};
